import Scene3D from 'apollo-e/Scene3D';
import CCContainer3D from '../components/CCContainer3D';
type ArcadeSimSnapshot = import('../sim/ArcadeSim').ArcadeSimSnapshot;

const CUBE_COLOR = { r: 1.0, g: 0.5, b: 0.1 };
const COLLISION_COLORS = [
  { r: 1, g: 1, b: 1 },
  { r: 0xc5 / 255, g: 0x79 / 255, b: 0xf4 / 255 },
  { r: 0x66 / 255, g: 0xbd / 255, b: 0xff / 255 },
  { r: 0xb1 / 255, g: 0xd9 / 255, b: 0xf6 / 255 },
  { r: 0xff / 255, g: 0x8a / 255, b: 0xd4 / 255 },
];

type CubeParticle = any;

/**
 * Client-only world rendering.
 *
 * IMPORTANT:
 * - This file must NOT step physics or consume deterministic RNG.
 * - It renders snapshots produced by the simulation (`src/sim/**`).
 */
export default class World {
  private readonly gameplayScene: Scene3D;
  private readonly depth: number;

  private readonly cubeContainer: CCContainer3D;
  private readonly cubeById: Map<number, CubeParticle> = new Map();

  constructor(textures: any, gameplayScene: Scene3D, depth: number) {
    this.gameplayScene = gameplayScene;
    this.depth = depth;

    const maxCubeCount = 512;
    this.cubeContainer = new CCContainer3D(['rotation', 'position', 'vertices', 'color'], maxCubeCount);
    this.gameplayScene.addObject(this.cubeContainer);
  }

  reset() {
    for (const particle of this.cubeById.values()) {
      this.cubeContainer.removeParticle(particle);
    }
    this.cubeById.clear();
  }

  /**
   * Client-only collision feedback (visuals only).
   * This was previously driven directly from physics collision callbacks.
   */
  playCollisionFx(opts?: { dramatic?: boolean }) {
    this._playCollisionFxOnParticles(Array.from(this.cubeById.values()), opts);
  }

  /**
   * Collision FX for the specific cube being collided with.
   * (If the cube is already gone, we fall back to applying the effect to all cubes.)
   */
  playCollisionFxForCubeId(cubeId: number | undefined, opts?: { dramatic?: boolean }) {
    if (cubeId === undefined || cubeId === null) {
      this.playCollisionFx(opts);
      return;
    }

    const p = this.cubeById.get(cubeId);
    if (!p) {
      this.playCollisionFx(opts);
      return;
    }

    this._playCollisionFxOnParticles([p], opts);
  }

  private _playCollisionFxOnParticles(particles: CubeParticle[], opts?: { dramatic?: boolean }) {
    const dramatic = opts?.dramatic !== false;
    const duration = dramatic ? 1000 : 800;

    if (particles.length === 0) {
      return;
    }

    // Drive the effect from the scene move system so it keeps ticking even if simulation is paused.
    this.gameplayScene.moves
      .move({})
      .lerp({}, duration)
      .ease((t: number) => {
        const k = 1 - t;
        for (let i = 0; i < particles.length; i++) {
          const cube = particles[i];
          const pivots = cube.pivots;
          const color = cube.color;

          const jitter = dramatic ? 0.15 : 0.05;
          pivots.x = 0.5 + (Math.random() - 0.5) * jitter * k;
          pivots.y = 0.5 + (Math.random() - 0.5) * jitter * k;

          if (dramatic) {
            // Include black while the effect is in progress, like the old behavior.
            const palette = t !== 1 ? COLLISION_COLORS.concat([{ r: 0, g: 0, b: 0 }]) : COLLISION_COLORS;
            const selected = palette[Math.floor(Math.random() * palette.length)];
            color.r = selected.r;
            color.g = selected.g;
            color.b = selected.b;
          } else {
            const keep = Math.round(Math.random());
            color.r = CUBE_COLOR.r * t + (CUBE_COLOR.r * keep + (1 - keep)) * (1 - t);
            color.g = CUBE_COLOR.g * t + (CUBE_COLOR.g * keep + (1 - keep)) * (1 - t);
            color.b = CUBE_COLOR.b * t + (CUBE_COLOR.b * keep + (1 - keep)) * (1 - t);
          }
        }
      });
  }

  /**
   * Render cubes using interpolation between previous and current simulation snapshots.
   */
  render(prev: ArcadeSimSnapshot, curr: ArcadeSimSnapshot, alpha: number) {
    const t = Math.max(0, Math.min(1, alpha));

    // Ensure all cubes in `curr` exist as particles.
    const currIds = new Set<number>();
    for (let i = 0; i < curr.cubes.length; i++) {
      const c = curr.cubes[i];
      currIds.add(c.id);
      if (!this.cubeById.has(c.id)) {
        const p = this.cubeContainer.createParticle();
        p.color.r = CUBE_COLOR.r;
        p.color.g = CUBE_COLOR.g;
        p.color.b = CUBE_COLOR.b;
        p.pivot = 0.5;
        p.scale = c.dim;
        p.position.z = -this.depth / 2;
        this.cubeById.set(c.id, p);
      }
    }

    // Remove particles that no longer exist in `curr`.
    for (const [id, particle] of this.cubeById.entries()) {
      if (!currIds.has(id)) {
        this.cubeContainer.removeParticle(particle);
        this.cubeById.delete(id);
      }
    }

    // Build quick lookup for prev cube poses.
    const prevById = new Map<number, { x: number; y: number; angle: number }>();
    for (let i = 0; i < prev.cubes.length; i++) {
      const c = prev.cubes[i];
      prevById.set(c.id, { x: c.x, y: c.y, angle: c.angle });
    }

    // Interpolate poses.
    for (let i = 0; i < curr.cubes.length; i++) {
      const c = curr.cubes[i];
      const particle = this.cubeById.get(c.id);
      if (!particle) continue;

      const p = prevById.get(c.id) ?? { x: c.x, y: c.y, angle: c.angle };
      particle.position.x = p.x + (c.x - p.x) * t;
      particle.position.y = p.y + (c.y - p.y) * t;
      particle.rotation.z = this._lerpAngle(p.angle, c.angle, t);
    }
  }

  private _lerpAngle(a: number, b: number, t: number) {
    let d = (b - a) % (Math.PI * 2);
    if (d > Math.PI) d -= Math.PI * 2;
    if (d < -Math.PI) d += Math.PI * 2;
    return a + d * t;
  }
}


